import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import useAxios from "../../../../../../hooks/useAxios";
import { API_DATASETS_DIMENSIONS } from "../../../../../../shared/lib/apiEndpointsResolver";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../../shared/components/dialog/Dialog";
import UnexpectedBackendError from "../../../../../../shared/components/UnexpectedBackendError";
import { Label } from "../../../../../../shared/components/label/Label";
import { Input } from "../../../../../../shared/components/input/Input";
import OptionsDropdown from "./OptionsDropdown";
import { Textarea } from "../../../../../../shared/components/textarea/Textarea";
import { Button } from "../../../../../../shared/components/button/Button";

const dimensionFormInitialState = {
  code: "",
  columnName: "",
  name: { en: "", es: "" },
  description: { en: "", es: "" },
  dataType: "varchar",
};

const AddDimensionDialog = ({
  datasetId,
  dimensionTypes,
  isDimensionDialogOpen,
  setIsDimensionDialogOpen,
  onAddDimensionSuccessfully,
  checkDimensionCodeDuplicated,
  checkDimensionColumnNameDuplicated,
}) => {
  const { t } = useTranslation();
  const axios = useAxios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [dimensionForm, setDimensionForm] = useState(dimensionFormInitialState);
  const [unexpectedError, setUnexpectedError] = useState(false);

  const validateDimensionForm = (form) => {
    const errors = {};

    if (!form.code.trim()) {
      errors.code = t("datasetsCentre.datasets.dimensions.codeRequired");
    } else if (checkDimensionCodeDuplicated(form.code.trim())) {
      errors.code = t("datasetsCentre.datasets.dimensions.codeExists");
    }

    if (!form.columnName.trim()) {
      errors.columnName = t(
        "datasetsCentre.datasets.dimensions.columnNameRequired",
      );
    } else if (checkDimensionColumnNameDuplicated(form.columnName.trim())) {
      errors.columnName = t(
        "datasetsCentre.datasets.dimensions.columnNameExists",
      );
    }

    if (!form.name.en.trim()) {
      errors.nameEn = t("datasetsCentre.datasets.dimensions.nameEnRequired");
    }

    if (!form.name.es.trim()) {
      errors.nameEs = t("datasetsCentre.datasets.dimensions.nameEsRequired");
    }

    if (!form.description.en.trim()) {
      errors.descriptionEn = t(
        "datasetsCentre.datasets.dimensions.descriptionEnRequired",
      );
    }

    if (!form.description.es.trim()) {
      errors.descriptionEs = t(
        "datasetsCentre.datasets.dimensions.descriptionEsRequired",
      );
    }

    return errors;
  };

  const handleAddDimension = async () => {
    const errors = validateDimensionForm(dimensionForm);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.post(
        API_DATASETS_DIMENSIONS(datasetId),
        dimensionForm,
      );

      if (result.status === 200) {
        onAddDimensionSuccessfully(result.data);
        closeDialog();
      } else {
        console.error(result.data);
        setUnexpectedError(true);
      }
    } catch (error) {
      console.error(error);
      setUnexpectedError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    setIsDimensionDialogOpen(false);
    setDimensionForm(dimensionFormInitialState);
    setFormErrors({});
  };

  return (
    <Dialog
      open={isDimensionDialogOpen}
      onOpenChange={setIsDimensionDialogOpen}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("datasetsCentre.datasets.dimensions.addTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("datasetsCentre.datasets.dimensions.addDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {unexpectedError && <UnexpectedBackendError />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dim-code">
                {t("datasetsCentre.common.code")} *
              </Label>
              <Input
                id="dim-code"
                value={dimensionForm.code}
                onChange={(e) =>
                  setDimensionForm((prev) => ({
                    ...prev,
                    code: e.target.value,
                  }))
                }
                placeholder="dimension_code"
                className={formErrors.code ? "border-destructive" : ""}
              />
              {formErrors.code && (
                <p className="text-sm text-destructive">{formErrors.code}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dim-column">
                {t("datasetsCentre.common.columnName")} *
              </Label>
              <Input
                id="dim-column"
                value={dimensionForm.columnName}
                onChange={(e) =>
                  setDimensionForm((prev) => ({
                    ...prev,
                    columnName: e.target.value,
                  }))
                }
                placeholder="column_name"
                className={formErrors.columnName ? "border-destructive" : ""}
              />
              {formErrors.columnName && (
                <p className="text-sm text-destructive">
                  {formErrors.columnName}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dim-datatype">
              {t("datasetsCentre.common.dataType")} *
            </Label>
            <OptionsDropdown
              value={dimensionForm.dataType}
              onValueChange={(value) =>
                setDimensionForm((prev) => ({
                  ...prev,
                  dataType: value,
                }))
              }
              items={dimensionTypes}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dim-name-en">
                {t("datasetsCentre.common.nameEn")} *
              </Label>
              <Input
                id="dim-name-en"
                value={dimensionForm.name.en}
                onChange={(e) =>
                  setDimensionForm((prev) => ({
                    ...prev,
                    name: { ...prev.name, en: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.dimensions.addNameEnPlaceholder",
                )}
                className={formErrors.nameEn ? "border-destructive" : ""}
              />
              {formErrors.nameEn && (
                <p className="text-sm text-destructive">{formErrors.nameEn}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dim-name-es">
                {t("datasetsCentre.common.nameEs")} *
              </Label>
              <Input
                id="dim-name-es"
                value={dimensionForm.name.es}
                onChange={(e) =>
                  setDimensionForm((prev) => ({
                    ...prev,
                    name: { ...prev.name, es: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.dimensions.addNameEsPlaceholder",
                )}
                className={formErrors.nameEs ? "border-destructive" : ""}
              />
              {formErrors.nameEs && (
                <p className="text-sm text-destructive">{formErrors.nameEs}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dim-desc-en">
                {t("datasetsCentre.common.descriptionEn")} *
              </Label>
              <Textarea
                id="dim-desc-en"
                value={dimensionForm?.description?.en}
                onChange={(e) =>
                  setDimensionForm((prev) => ({
                    ...prev,
                    description: { ...prev.description, en: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.dimensions.addDescriptionEnPlaceholder",
                )}
                rows={3}
              />
              {formErrors.descriptionEn && (
                <p className="text-sm text-destructive">
                  {formErrors.descriptionEn}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dim-desc-es">
                {t("datasetsCentre.common.descriptionEs")} *
              </Label>
              <Textarea
                id="dim-desc-es"
                value={dimensionForm.description.es}
                onChange={(e) =>
                  setDimensionForm((prev) => ({
                    ...prev,
                    description: { ...prev.description, es: e.target.value },
                  }))
                }
                placeholder={t(
                  "datasetsCentre.datasets.dimensions.addDescriptionEsPlaceholder",
                )}
                rows={3}
              />
              {formErrors.descriptionEs && (
                <p className="text-sm text-destructive">
                  {formErrors.descriptionEs}
                </p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={isSubmitting}
          >
            {t("datasetsCentre.common.cancel")}
          </Button>
          <Button onClick={handleAddDimension} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("datasetsCentre.common.adding")}
              </>
            ) : (
              t("datasetsCentre.datasets.dimensions.addButton")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDimensionDialog;
